export const useCurrency = () => {
  const { currency: merchantCurrency } = useTenant()

  /**
   * Formats a minor unit value using the currency's precision, prefixing GBP/EUR
   * symbols and suffixing other codes, with optional compact and zero-stripped output.
   */
  const formatToCurrencyString = (
    price: number,
    currencyCode: string,
    {
      compact = false,
      stripTrailingZeros = false,
    }: {
      compact?: boolean
      stripTrailingZeros?: boolean
    } = {}
  ): string => {
    const localeBcp47 = "en-GB"
    const normalizedCurrencyCode =
      currencyCode.toUpperCase()
    const currencyFractionDigits =
      new Intl.NumberFormat(localeBcp47, {
        style: "currency",
        currency: normalizedCurrencyCode,
      }).resolvedOptions().maximumFractionDigits ?? 2
    const amountInMajorUnits =
      price / 10 ** currencyFractionDigits
    const maximumFractionDigits =
      stripTrailingZeros &&
      Number.isInteger(amountInMajorUnits)
        ? 0
        : currencyFractionDigits
    const formattedAmount = new Intl.NumberFormat(
      localeBcp47,
      {
        maximumFractionDigits,
        minimumFractionDigits: maximumFractionDigits,
        ...(compact && {
          notation: "compact",
        }),
      }
    ).format(amountInMajorUnits)

    const currencySymbol =
      normalizedCurrencyCode === "GBP"
        ? "£"
        : normalizedCurrencyCode === "EUR"
          ? "€"
          : undefined

    if (!currencySymbol) {
      return `${formattedAmount} ${normalizedCurrencyCode}`
    }

    return formattedAmount.startsWith("-")
      ? `-${currencySymbol}${formattedAmount.slice(1)}`
      : `${currencySymbol}${formattedAmount}`
  }

  const formatToMerchantCurrencyString = (
    price: number,
    {
      compact = false,
      stripTrailingZeros = false,
    }: {
      compact?: boolean
      stripTrailingZeros?: boolean
    } = {}
  ): string => {
    return formatToCurrencyString(
      price,
      merchantCurrency.value,
      {
        compact,
        stripTrailingZeros,
      }
    )
  }

  const merchantCurrencySymbol = computed(() =>
    merchantCurrency.value === "GBP"
      ? "£"
      : merchantCurrency.value
        ? "€"
        : merchantCurrency.value
  )

  const getCurrencySymbol = (currencyCode: string) =>
    currencyCode === "GBP"
      ? "£"
      : currencyCode
        ? "€"
        : currencyCode

  return {
    formatToMerchantCurrencyString,
    merchantCurrencySymbol,
    getCurrencySymbol,
    formatToCurrencyString,
  }
}
